import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../components/ui/Toast';

// Simulate style transformation for preview
const simulateTransform = (style, text) => {
  if (!text) return { linkedin: '', twitter: '' };
  const short = text.substring(0, 120);

  if (style?.id === 'mentor' || style?.tone?.toLowerCase()?.includes('educational')) {
    return {
      linkedin: `Let me share an insight: ${text}\n\nThis demonstrates a key principle in software development. Understanding the underlying mechanics leads to better decisions.\n\nHere's what I learned:\n• The approach matters more than the tool\n• Performance gains compound over time\n• Always measure before optimizing\n\nWhat techniques have worked for you?`,
      twitter: `Quick lesson: ${short}...\n\nThe key? Understanding the fundamentals first.\n\nWhat's your go-to strategy?`
    };
  } else if (style?.id === 'deep-dive' || style?.technicalDepth === 'High') {
    return {
      linkedin: `Technical Deep Dive: ${text}\n\nLet's break down the implementation:\n\n1. Architecture Considerations\nThe underlying mechanism involves several key components working in concert.\n\n2. Performance Implications\nBenchmark results show significant improvements across multiple metrics.\n\n3. Best Practices\n- Implement lazy loading patterns\n- Monitor bundle size metrics\n- Use code splitting strategically\n\nFull breakdown in comments. 👇`,
      twitter: `Deep dive: ${short}\n\nKey findings:\n→ 40% performance gain\n→ Reduced memory footprint\n→ Better UX metrics\n\nThread 🧵👇`
    };
  } else {
    return {
      linkedin: `${text}\n\nResult: Better performance. Smaller bundles. Faster loads.\n\nImplementation is straightforward. Impact is significant.`,
      twitter: `${short}\n\nSimple. Effective. Done.`
    };
  }
};

const StyleModal = ({ isOpen, onClose, style, isEditing: initialEditing, onSave, onSetDefault, onDelete, isDefault }) => {
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(initialEditing);
  const [formData, setFormData] = useState({
    name: '', description: '', tone: '', technicalDepth: 'Medium', engagement: '', vocabulary: '', contentStructure: ''
  });

  // Preview state
  const [previewInput, setPreviewInput] = useState('Discovered a powerful React optimization technique that reduced our bundle size by 40%. The key was implementing dynamic imports with React.lazy() and strategic code splitting.');
  const [linkedinPreview, setLinkedinPreview] = useState('');
  const [twitterPreview, setTwitterPreview] = useState('');
  const [isTransforming, setIsTransforming] = useState(false);
  const linkedinRef = useRef(null);
  const twitterRef = useRef(null);

  const displayName = userProfile?.full_name || user?.email?.split('@')?.[0] || 'User';
  const displayAvatar = userProfile?.avatar_url || user?.user_metadata?.avatar_url || '';

  // Auto-grow textareas when content changes
  useEffect(() => {
    [linkedinRef, twitterRef].forEach(ref => {
      if (ref.current) {
        ref.current.style.height = 'auto';
        ref.current.style.height = ref.current.scrollHeight + 'px';
      }
    });
  }, [linkedinPreview, twitterPreview]);

  useEffect(() => {
    setIsEditing(initialEditing);
  }, [initialEditing]);

  useEffect(() => {
    if (style) {
      setFormData({
        name: style.name || '', description: style.description || '',
        tone: style.tone || '', technicalDepth: style.technicalDepth || 'Medium',
        engagement: style.engagement || '', vocabulary: style.vocabulary || '',
        contentStructure: style.contentStructure || '',
      });
      // Generate initial preview
      if (!initialEditing) {
        generatePreview(style, previewInput);
      }
    }
  }, [style, initialEditing]);

  const generatePreview = (styleData, text) => {
    setIsTransforming(true);
    setTimeout(() => {
      const result = simulateTransform(styleData, text);
      setLinkedinPreview(result.linkedin);
      setTwitterPreview(result.twitter);
      setIsTransforming(false);
    }, 800);
  };

  if (!isOpen) return null;

  const handleSave = (e) => {
    e?.preventDefault();
    if (!formData.name.trim()) { toast.error('Style name is required'); return; }
    onSave({ ...style, ...formData, isCustom: true });
    onClose();
  };

  const handlePreviewInputChange = (val) => {
    setPreviewInput(val);
    generatePreview(style, val);
  };

  // Auto-grow textareas
  const autoGrow = (ref) => {
    if (ref?.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = Math.max(120, ref.current.scrollHeight) + 'px';
    }
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-5xl mx-4 max-h-[90vh] bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${style?.color || '#A78BFA'}15` }}>
              <Icon name={style?.icon || 'Sparkles'} size={18} color={style?.color || '#A78BFA'} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">
                {isEditing ? (style?.name ? `Edit: ${style.name}` : 'Create New Style') : style?.name}
              </h2>
              {!isEditing && style?.description && (
                <p className="text-xs text-muted-foreground">{style.description}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isEditing && !isDefault && (
              <button onClick={() => { onSetDefault(style?.id); onClose(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted rounded-lg transition-all">
                <Icon name="Star" size={13} /> Set Default
              </button>
            )}
            {isDefault && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-400 bg-violet-500/10 rounded-lg">
                <Icon name="Star" size={13} /> Default
              </span>
            )}
            {!isEditing && style?.isCustom && (
              <>
                <button onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted rounded-lg transition-all">
                  <Icon name="Pencil" size={13} /> Edit
                </button>
                <button onClick={() => { onDelete(style?.id); onClose(); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-all">
                  <Icon name="Trash2" size={13} />
                </button>
              </>
            )}
            <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {isEditing ? (
            /* ── EDIT MODE ── */
            <form onSubmit={handleSave} id="style-form" className="space-y-4 max-w-xl mx-auto">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Style Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., The Technical Storyteller" required
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-violet-500 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Description *</label>
                <input type="text" value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                  placeholder="Brief description of this writing style" required
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-violet-500 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Tone</label>
                <input type="text" value={formData.tone} onChange={(e) => setFormData(p => ({ ...p, tone: e.target.value }))}
                  placeholder="e.g., Professional, conversational, enthusiastic"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-violet-500 transition-colors" />
                <p className="text-[10px] text-muted-foreground mt-1">Define the overall tone and voice</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Technical Depth</label>
                <select value={formData.technicalDepth} onChange={(e) => setFormData(p => ({ ...p, technicalDepth: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-violet-500 transition-colors">
                  <option value="Low">Low — Beginner friendly</option>
                  <option value="Medium">Medium — Intermediate</option>
                  <option value="High">High — Advanced technical</option>
                </select>
                <p className="text-[10px] text-muted-foreground mt-1">How technical should the content be?</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Engagement Strategy</label>
                <input type="text" value={formData.engagement} onChange={(e) => setFormData(p => ({ ...p, engagement: e.target.value }))}
                  placeholder="e.g., Questions, call-to-actions, storytelling"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-violet-500 transition-colors" />
                <p className="text-[10px] text-muted-foreground mt-1">How to engage with the audience</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Preferred Vocabulary</label>
                <input type="text" value={formData.vocabulary} onChange={(e) => setFormData(p => ({ ...p, vocabulary: e.target.value }))}
                  placeholder="e.g., Industry jargon, simple terms, metaphors"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-violet-500 transition-colors" />
                <p className="text-[10px] text-muted-foreground mt-1">Vocabulary preferences and terminology</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Content Structure</label>
                <input type="text" value={formData.contentStructure} onChange={(e) => setFormData(p => ({ ...p, contentStructure: e.target.value }))}
                  placeholder="e.g., Problem-solution, step-by-step, narrative"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-violet-500 transition-colors" />
                <p className="text-[10px] text-muted-foreground mt-1">Preferred content organization pattern</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={onClose}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-muted-foreground bg-muted hover:bg-muted rounded-lg transition-all">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] text-white rounded-lg hover:opacity-90 transition-all">
                  <Icon name="Save" size={14} /> Save Style
                </button>
              </div>
            </form>
          ) : (
            /* ── VIEW MODE — attributes + editable preview ── */
            <div className="space-y-6">
              {/* Attribute chips */}
              <div className="flex flex-wrap gap-2">
                {style?.tone && style.tone.split(',').map((t, i) => (
                  <span key={`tone-${i}`} className="px-2.5 py-1 bg-muted border border-border rounded-full text-xs text-foreground">
                    {t.trim()}
                  </span>
                ))}
                {style?.technicalDepth && (
                  <span className="px-2.5 py-1 bg-violet-500/10 border border-violet-500/20 rounded-full text-xs text-violet-300">
                    Depth: {style.technicalDepth}
                  </span>
                )}
                {style?.engagement && style.engagement.split(',').map((e, i) => (
                  <span key={`eng-${i}`} className="px-2.5 py-1 bg-muted border border-border rounded-full text-xs text-foreground">
                    {e.trim()}
                  </span>
                ))}
                {style?.usageCount !== undefined && (
                  <span className="px-2.5 py-1 bg-muted border border-border rounded-full text-xs text-muted-foreground">
                    Used {style.usageCount} times
                  </span>
                )}
              </div>

              {/* Preview Input */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Preview Input</label>
                <textarea
                  value={previewInput}
                  onChange={(e) => handlePreviewInputChange(e.target.value)}
                  placeholder="Enter sample text to see how this style transforms it..."
                  rows={2}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-violet-500 transition-colors resize-none"
                />
              </div>

              {/* LinkedIn & X Preview — Edit & Preview layout */}
              {isTransforming ? (
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader" size={24} className="animate-spin text-violet-400" />
                  <span className="ml-3 text-sm text-muted-foreground">Transforming...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* LinkedIn */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Edit */}
                    <div className="bg-card rounded-xl border border-border overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
                        <div className="flex items-center gap-2">
                          <Icon name="Linkedin" size={14} color="#0A66C2" />
                          <span className="text-xs font-medium text-foreground">LinkedIn Edit</span>
                        </div>
                        <span className="text-xs font-mono text-muted-foreground">{linkedinPreview.length}/3000</span>
                      </div>
                      <textarea
                        ref={linkedinRef}
                        value={linkedinPreview}
                        onChange={(e) => { setLinkedinPreview(e.target.value); }}
                        className="w-full px-4 py-3 bg-transparent text-foreground text-sm leading-relaxed resize-none focus:outline-none"
                        style={{ fontFamily: "'Source Sans 3', sans-serif" }}
                      />
                    </div>
                    {/* Preview */}
                    <div className="preview-glow"><div className="bg-card rounded-[12.5px] overflow-hidden">
                      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
                        <Icon name="Linkedin" size={14} color="#0A66C2" />
                        <span className="text-xs font-medium text-foreground">LinkedIn Preview</span>
                      </div>
                      <div className="p-4">
                        <div className="flex items-start gap-3 mb-3">
                          {displayAvatar ? (
                            <Image src={displayAvatar} alt={displayName} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                              <Icon name="User" size={14} className="text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <h4 className="text-sm font-semibold text-foreground">{displayName}</h4>
                            <p className="text-xs text-muted-foreground">Just now</p>
                          </div>
                        </div>
                        <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{linkedinPreview}</div>
                        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
                          <span className="text-xs text-muted-foreground"><Icon name="ThumbsUp" size={12} /> Like</span>
                          <span className="text-xs text-muted-foreground"><Icon name="MessageCircle" size={12} /> Comment</span>
                          <span className="text-xs text-muted-foreground"><Icon name="Repeat2" size={12} /> Repost</span>
                        </div>
                      </div>
                    </div></div>
                  </div>

                  {/* X/Twitter */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Edit */}
                    <div className="bg-card rounded-xl border border-border overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
                        <div className="flex items-center gap-2">
                          <Icon name="X" size={14} className="text-foreground" />
                          <span className="text-xs font-medium text-foreground">X Edit</span>
                        </div>
                        <span className={`text-xs font-mono ${twitterPreview.length > 280 ? 'text-red-400' : 'text-muted-foreground'}`}>{twitterPreview.length}/280</span>
                      </div>
                      <textarea
                        ref={twitterRef}
                        value={twitterPreview}
                        onChange={(e) => { setTwitterPreview(e.target.value); }}
                        className="w-full px-4 py-3 bg-transparent text-foreground text-sm leading-relaxed resize-none focus:outline-none"
                        style={{ fontFamily: "'Source Sans 3', sans-serif" }}
                      />
                    </div>
                    {/* Preview */}
                    <div className="preview-glow"><div className="bg-card rounded-[12.5px] overflow-hidden">
                      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
                        <Icon name="X" size={14} className="text-foreground" />
                        <span className="text-xs font-medium text-foreground">X Preview</span>
                      </div>
                      <div className="p-4">
                        <div className="flex items-start gap-3 mb-3">
                          {displayAvatar ? (
                            <Image src={displayAvatar} alt={displayName} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                              <Icon name="User" size={14} className="text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <h4 className="text-sm font-semibold text-foreground">{displayName}</h4>
                            <p className="text-xs text-muted-foreground">Just now</p>
                          </div>
                        </div>
                        <div className="text-sm text-foreground leading-normal whitespace-pre-wrap">{twitterPreview}</div>
                        <div className="flex items-center gap-6 mt-3 pt-3 border-t border-border">
                          <span className="text-xs text-muted-foreground"><Icon name="Heart" size={12} /> 0</span>
                          <span className="text-xs text-muted-foreground"><Icon name="MessageCircle" size={12} /> 0</span>
                          <span className="text-xs text-muted-foreground"><Icon name="Repeat2" size={12} /> 0</span>
                        </div>
                      </div>
                    </div></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StyleModal;
